<template>
  <Section 
  :background="content.type === 'center' ? white : ''"
  :class="content.type === 'center' ? '' : '!my-0'"
  >
    <Container>
      <FadeIn v-if="content.type === 'center'">
        <div class="text-center mx-auto max-w-2xl">
          <h2 class="text-section-title">{{ content.title }}</h2>
          <div 
          v-if="content.subtitle"
          v-interpolation 
          v-html="useSanitize(content.subtitle, currentLocale)" 
          class="text-sm sm:text-base lg:leading-[1.75rem] mt-2 sm:mt-4 prose prose-trust">
          </div>
          
          <div class="text-center mt-8 lg:mt-12">
            <img
            :src="useCdn('74edf326-f927-4feb-a180-31a49f65d64c', 'iso-27001-certified.svg')"
            alt="ISO 27001 Certified"
            class="h-16 w-auto inline-block mr-6"
            loading="lazy"
            >
            <img
            :src="useCdn('5f1bdd7e-b5d7-4409-b80b-9c24ebafd34e', 'gdpr.png')"
            alt="GDPR Certified"
            class="h-16 w-16 inline-block"
            loading="lazy"
            >
          </div>
          <div class="text-center mt-12 lg:mt-16">
            <ElementButton
            url="/security"
            type="black"
            :arrow="true"
            >
              Security page
            </ElementButton>
          </div>
        </div>
      </FadeIn>
      <FadeIn v-else>
        <div class="rounded-[20px] md:rounded-[35px] border-2 border-[#c4d791] bg-[#E1F4AD] px-4 md:px-8 py-6 md:py-12">
          <div class="md:flex md:gap-8 lg:gap-12">
            <div class="md:w-[60%] lg:w-[50%] xl:w-[60%]">
              <h2 class="text-section-title">{{ content.title }}</h2>
              <div 
              v-if="content.subtitle"
              v-interpolation 
              v-html="useSanitize(content.subtitle, currentLocale)" 
              class="text-sm sm:text-base lg:leading-[1.75rem] mt-4 sm:mt-8 prose prose-trust">
              </div>
              <div 
              v-if="content?.security_link"
              class="mt-8">
                <button
                @click="modalOpen = true"
                data-cta
                data-cta-label="Request documentation"
                class="rounded-md block sm:inline-block bg-[#111827] px-3.5 py-2.5 text-sm font-semibold text-white font-semibold focus:outline-none"
                >
                  Request documentation
                </button>
                <ElementButton
                v-if="route.path !== '/security'"
                url="/security"
                :arrow="true"
                class="block sm:inline-block !text-[#111827] mt-4 sm:mt-0 sm:ml-6 text-sm font-semibold focus:outline-none"
                >
                  Security page
                </ElementButton>
              </div>
            </div>
            <div 
            class="md:mt-0 md:w-[40%] lg:w-[50%] xl:w-[40%] flex md:justify-center md:items-center"
            :class="content?.security_link ? 'mt-16' : 'mt-8'"
            >
              <div class="max-w-[175px] min-w-[150px] sm:max-w-[40%] lg:max-w-[50%]">
                <img
                :src="useCdn('74edf326-f927-4feb-a180-31a49f65d64c', 'iso-27001-certified.svg')"
                alt="ISO 27001 Certified"
                class="h-16 lg:h-24 inline-block mb-2 md:mb-6 w-auto inline-block mr-6 lg:mr-12"
                loading="lazy"
                width="227"
                height="102"
                >
                <img
                :src="useCdn('c39b77d4-a682-489b-8c92-e07bfbc4b5ad', 'soc-2-verified.svg')"
                alt="SOC 2 Verified"
                class="h-16 lg:h-24 block w-auto inline-block mr-6 lg:mr-12"
                loading="lazy"
                width="227"
                height="102"
                >
              </div>
              <div class="max-w-[60%] lg:max-w-[50%]">
                <img
                :src="useCdn('5f1bdd7e-b5d7-4409-b80b-9c24ebafd34e', 'gdpr.png')"
                alt="GDPR Certified"
                class="h-16 w-16 lg:h-24 lg:ml-8 lg:w-24 inline-block"
                loading="lazy"
                width="102"
                height="102"
                >
              </div>

            </div>
          </div>

        </div>
      </FadeIn>
    </Container>
  </Section>

  <!-- Security Documentation Modal -->
  <TransitionRoot as="template" :show="modalOpen">
    <Dialog as="div" class="relative z-10" @close="modalOpen = false">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full justify-center p-4 text-center items-center">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              
              <button 
                @click="modalOpen = false" 
                class="absolute top-4 right-4 text-gray-900 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-1"
                aria-label="Close dialog"
              >
                <Icon 
                name="iconoir:xmark" 
                class="w-6 h-6"
                aria-hidden="true"
                />
              </button>
              
              <section class="p-4 md:p-6 lg:p-8" v-if="!sent">
                <div class="max-w-xl mx-auto">
                  <DialogTitle as="h2" class="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                    Request security documentation
                  </DialogTitle>

                  <form @submit.prevent="submitForm" class="space-y-6">
                    <!-- Full Name -->
                    <div>
                      <label for="modal-name" class="text-lg font-bold text-gray-900">Full name</label>
                      <div class="mt-2.5">
                        <input 
                        type="text" 
                        v-model="name" @focus="formTracking.start()" 
                        id="modal-name" 
                        placeholder="Your Name" 
                        autocomplete="name" 
                        class="input-text" 
                        required
                        />
                      </div>
                    </div>

                    <!-- Organisation -->
                    <div>
                      <label for="modal-organization" class="text-lg font-bold text-gray-900">Organisation</label>
                      <div class="mt-2.5">
                        <input 
                        type="text" 
                        v-model="organization" @focus="formTracking.start()" 
                        id="modal-organization" 
                        placeholder="Organisation Name" 
                        autocomplete="organization" 
                        class="input-text" 
                        required
                        />
                      </div>
                    </div>

                    <!-- Email -->
                    <div>
                      <label for="modal-email" class="text-lg font-bold text-gray-900">Work email</label>
                      <div class="mt-2.5">
                        <input 
                        type="email" 
                        v-model="email" @focus="formTracking.start()" 
                        id="modal-email" 
                        autocomplete="email" 
                        placeholder="yourname@example.com" 
                        class="input-text" 
                        required
                        />
                      </div>
                    </div>

                    <!-- Message -->
                    <div>
                      <label for="modal-message" class="text-lg font-bold text-gray-900">Message</label>
                      <div class="mt-2.5">
                        <textarea 
                        id="modal-message" 
                        v-model="message" @focus="formTracking.start()" 
                        rows="4" 
                        placeholder="Please tell us what security documentation you need" 
                        class="input-text"
                        required
                        ></textarea>
                      </div>
                    </div>

                    <!-- Honeypot field - hidden from users, bots will fill it -->
                    <div aria-hidden="true" style="position: absolute; left: -9999px;">
                      <label for="modal-website">Website</label>
                      <input type="text" v-model="website" id="modal-website" name="website" tabindex="-1" autocomplete="off" />
                    </div>

                    <!-- Consent -->
                    <div class="mt-6">
                      <div class="flex">
                        <input 
                        @click="errorMessage = null"
                        id="modal-consent" 
                        v-model="consent" 
                        type="checkbox" 
                        class="h-5 w-5 mt-[2px] cursor-pointer rounded border-[#E4E2DF] text-[#4d5ceb] focus:ring-transparent"
                        required
                        />
                        <label @click="errorMessage = null" for="modal-consent" class="ml-2 block text-sm cursor-pointer">
                          I confirm I have read and agree to the processing of my above personal data as described in the <NuxtLink :to="$localePath('/policies/privacy')" class="underline" target="_blank">Privacy Policy</NuxtLink>.
                        </label>
                      </div> 
                    </div>

                    <!-- Submit Button -->
                    <div class="mt-8">
                      <button
                      type="submit"
                      :disabled="waiting"
                      class="w-full inline-flex items-center justify-center rounded-md border border-transparent bg-[#096b72] px-5 py-3 text-md font-semibold text-white hover:bg-[#07484b]"
                      :class="{ 'opacity-60' : waiting }"
                      >
                        <template v-if="waiting">
                          <ElementLoadingSpinner />
                          <span>{{ sendingMessage || 'Sending...' }}</span>
                        </template>
                        <template v-else>
                          Submit request
                        </template>
                      </button>
                      <ElementSubmitStatus :message="waiting ? sendingMessage : ''" />
                    </div>
                    
                    <div v-if="errorMessage" class="text-red-500 text-sm font-normal mt-4">
                      {{ errorMessage }}
                    </div>
                  </form>
                </div>
              </section>

              <section class="p-4 md:p-6 lg:p-8" v-else>
                <div class="max-w-xl mx-auto text-center">
                  <DialogTitle as="h2" class="text-3xl font-bold text-gray-900 mb-6">
                    Success
                  </DialogTitle>
                  <p class="mt-2 text-base text-gray-500">
                    Thank you for your request. We will send you the security documentation within one business day.
                  </p>
                </div>
              </section>

            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'

const props = defineProps({
  content: {
    type: Object,
    required: true,
  },
  sectionTotal: {
    type: Number,
    default: 0,
  },
  sectionIndex: {
    type: Number,
    default: 0,
  }
})

const route = useRoute()
const userStore = useUserStore()

const currentLocale = computed(() => {
  return route.path.startsWith('/nz') ? 'nz' : 'au'
})

// Modal state
const modalOpen = ref(false)
const sent = ref(false)
const waiting = ref(false)
const errorMessage = ref(null)

// Form fields
const name = ref('')
const email = ref('')
const organization = ref('')
const message = ref('')
const consent = ref(false)
const website = ref('') // Honeypot field
const formLoadedAt = ref(0) // Spam-detection timestamp; stamped when the modal opens

// Reset form when modal opens
watch(modalOpen, (isOpen) => {
  if (isOpen && !sent.value) {
    name.value = ''
    email.value = ''
    organization.value = ''
    message.value = ''
    consent.value = false
    website.value = ''
    formLoadedAt.value = Date.now() // The spam clock starts when the modal opens
    errorMessage.value = null
    // The tracker is created once at setup but the form is cleared and reshown,
    // so start a fresh attempt cycle or a reopened-and-submitted modal would
    // record a submit with no preceding start.
    formTracking.reset()
  }
  if (!isOpen) {
    sent.value = false
  }
})

const formTracking = useFormTracking('security-request')

const submitForm = async (e) => {
  e.preventDefault()
  waiting.value = true
  errorMessage.value = null
  formTracking.submitted()

  // Validation - organization is mandatory
  if (!name.value || !email.value || !organization.value || !message.value) {
    errorMessage.value = 'Please fill out all fields before submitting the form.'
    formTracking.failed('missing-required-fields')
    waiting.value = false
    return false
  }

  if (consent.value !== true) {
    errorMessage.value = 'To continue, please confirm we can use the above personal data in accordance with our Privacy Policy.'
    formTracking.failed('consent-not-given', 'consent')
    waiting.value = false
    return false
  }

  const data = {
    name: name.value,
    email: email.value,
    organization: organization.value,
    'marketing_status': 'subscribed',
    message: '<p>Security documentation request:</p>' + message.value,
    // Explicit CRM key; /api/elkerContact allowlists it and maps everything
    // else (including the contact form's legacy 59) to 'contact'.
    webformId: 'security_request',
    utmSource: userStore.utmSource,
    utmMedium: userStore.utmMedium,
    utmCampaign: userStore.utmCampaign,
    utmLandingPage: userStore.utmLandingPage,
    // Conversion attribution: this posts to /api/elkerContact, which now fires
    // the server-side lead conversion, so it must carry the click ids too.
    ...useConversionFields(),
    // Spam protection
    website: website.value,
    formLoadedAt: formLoadedAt.value
  }

  startSendingMessages()

  try {
    const addUserRes = await $fetch('/api/elkerContact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (addUserRes.ok) {
      sent.value = true
      formTracking.ok({ recorded: addUserRes.recorded !== false })

      // recorded: false marks a server-side decoy (spam gate tripped): show
      // the success state as normal but do not fire conversion events for it.
      if (process.client && window.dataLayer && addUserRes.recorded !== false) {
        window.dataLayer.push({
          event: 'elker_conversion',
          'enhanced_conversion_email': email.value
        })
        window.dataLayer.push({
          event: 'contact_form_submitted',
          // Additive param so GTM can tell security requests from general
          // enquiries; the event name stays put because live GTM triggers
          // (configured outside this repo) key on it.
          form_type: 'security_request'
        })
      }
    } else {
      // Same as contact.vue: a 200 carrying {ok:false} does not throw, so
      // without this branch a server-side failure produced an attempt with no
      // terminal event and no message for the visitor.
      errorMessage.value = addUserRes.message || 'There has been an error submitting the form. Please email us at hello@elker.com'
      formTracking.failed('server-rejected')
    }

  } catch(e) {
    if (!sent.value) {
      errorMessage.value = 'There has been an error submitting the form. Please email us at hello@elker.com'
      formTracking.failed('request-failed')
    }
  } finally {
    stopSendingMessages()
    waiting.value = false
  }
}

const { sendingMessage, startSendingMessages, stopSendingMessages } = useSubmitProgress()
</script>

<style lang="scss">
.prose.prose-trust {
  ul li {
    margin-bottom: 0!important;
    margin-top: 0!important;
    &::marker {
      color: #000;
      font-size: 1.25rem;
    }
  }
}
</style>
